import { useState, useEffect } from "react";
import { Plus, Trash2, Copy, FileDown, FileText, BarChart3, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { getStaffToken } from "../../lib/api";
import { useStaffAuth } from "../../context/StaffAuthContext";
import MediaField from "../../components/staff/MediaField";

/**
 * Staff — Lien d'inscription personnalisé (iter-68).
 *
 * Créer / suivre / exporter des liens d'inscription pour tout type de groupe
 * (entreprise, événement privé, sortie, séminaire…). Chaque lien est plafonné
 * par un nombre max de participants — dès que le quota est atteint, le lien
 * public devient automatiquement inactif.
 */
const PAYMENT_MODE_LABEL = {
  free: "Gratuit — pas de paiement",
  paid: "Payant — paiement en ligne",
  configurable: "Configurable par le participant",
};

const EMPTY_FORM = {
  group_name: "",
  description: "",
  reservation_type: "",
  event_date: "",
  event_time: "",
  boat_time: "",
  cover_image: "",
  max_participants: 20,
  payment_mode: "configurable",
  price_mode: "per_offer",
  price_amount: 0,
  contact_email: "",
  contact_phone: "",
  notes: "",
};

export default function StaffCorporateRequests() {
  const { user } = useStaffAuth();
  const isManager = ["manager", "admin"].includes(user?.role);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/corporate-requests");
      setRequests(data.items || []);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!form.group_name?.trim() || !form.reservation_type?.trim()) {
      toast.error("Nom du groupe et type d'événement requis");
      return;
    }
    if (form.payment_mode === "paid" && form.price_mode === "fixed") {
      if (!form.price_amount || form.price_amount <= 0) {
        toast.error("Renseignez un montant pour le mode payant (prix fixe)");
        return;
      }
    }
    try {
      const payload = { ...form };
      // Strip empties
      ["event_date", "event_time", "boat_time", "cover_image",
       "contact_email", "contact_phone", "notes", "description"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (payload.payment_mode !== "paid" || payload.price_mode !== "fixed") {
        delete payload.price_amount;
      }
      await api.post("/staff/corporate-requests", payload);
      toast.success("Lien d'inscription créé");
      setCreating(false);
      setForm(EMPTY_FORM);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openDetail = async (id) => {
    try {
      const [d, s] = await Promise.all([
        api.get(`/staff/corporate-requests/${id}`),
        api.get(`/staff/corporate-requests/${id}/stats`),
      ]);
      setSelected(d.data);
      setStats(s.data);
    } catch {
      toast.error("Erreur de chargement détail");
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setStats(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce lien ? Tous les participants inscrits seront perdus.")) return;
    try {
      await api.delete(`/staff/corporate-requests/${id}`);
      toast.success("Supprimé");
      refresh();
      if (selected?.id === id) closeDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const publicUrl = (slugOrToken) =>
    `${window.location.origin}/inscription/${slugOrToken}`;

  const copyLink = (slugOrToken) => {
    navigator.clipboard.writeText(publicUrl(slugOrToken)).then(
      () => toast.success("Lien copié dans le presse-papiers"),
      () => toast.error("Impossible de copier — copiez manuellement"),
    );
  };

  const downloadExport = async (id, kind) => {
    try {
      const url = `${api.defaults.baseURL}/staff/corporate-requests/${id}/participants.${kind}`;
      const token = getStaffToken() || "";
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `participants.${kind}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Échec de l'export");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="staff-corporate-requests">
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-7">
        <div>
          <h1 className="font-display-serif text-3xl text-[#0A0A0A]">Lien d&apos;inscription personnalisé</h1>
          <p className="text-sm text-[#0A0A0A]/55 mt-1 max-w-2xl">
            Créez un lien d&apos;inscription pour tout type de groupe (entreprise, séminaire,
            événement privé, sortie…). Le quota se ferme automatiquement une fois le nombre
            maximum de participants atteint.
          </p>
        </div>
        {isManager && (
          <button
            onClick={() => setCreating(true)}
            className="btn-gold inline-flex items-center gap-2"
            data-testid="open-create-corporate"
          >
            <Plus size={14} /> Nouveau lien
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-[#0A0A0A]/55 text-sm">Chargement…</div>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-dashed border-[#0A0A0A]/15 p-12 text-center">
          <p className="text-sm text-[#0A0A0A]/55">Aucun lien d&apos;inscription pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="corporate-requests-list">
          {requests.map((r) => {
            const groupName = r.group_name || r.company_name || "—";
            const pct = r.max_participants > 0
              ? Math.min(100, Math.round((r.registered_count / r.max_participants) * 100))
              : 0;
            const color = r.is_full ? "#16A34A" : "#B8922A";
            return (
              <div
                key={r.id}
                className="bg-white border border-[#0A0A0A]/8 overflow-hidden hover:border-[#B8922A]/40 transition-colors"
                data-testid={`corporate-card-${r.id.slice(0, 8)}`}
              >
                {r.cover_image && (
                  <div className="h-28 w-full bg-[#FAFAF7] overflow-hidden">
                    <img src={r.cover_image} alt={groupName} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="font-display-serif text-lg text-[#0A0A0A] truncate">{groupName}</div>
                      <div className="text-[0.7rem] text-[#0A0A0A]/55 mt-0.5">
                        {r.reservation_type}
                        {r.event_date ? ` · ${r.event_date}` : ""}
                        {r.event_time ? ` · ${r.event_time}` : ""}
                      </div>
                    </div>
                    <span
                      className={`text-[0.55rem] uppercase tracking-[0.18em] px-2 py-1 whitespace-nowrap ${
                        r.is_full ? "bg-green-50 text-green-700" :
                        r.status === "closed" ? "bg-[#0A0A0A]/5 text-[#0A0A0A]/55" : "bg-[#B8922A]/10 text-[#B8922A]"
                      }`}
                    >
                      {r.is_full ? "Complet" : r.status === "closed" ? "Clos" : "Ouvert"}
                    </span>
                  </div>
                  <div className="mb-4">
                    <div className="flex justify-between text-[0.72rem] text-[#0A0A0A]/65 mb-1">
                      <span>
                        <span className="font-medium text-[#0A0A0A]">{r.registered_count}</span> / {r.max_participants} inscrits
                      </span>
                      <span className="tabular-nums">{r.remaining_seats} restants</span>
                    </div>
                    <div className="h-1.5 bg-[#0A0A0A]/5 overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color, transition: "width 800ms ease-out" }} />
                    </div>
                  </div>
                  <div className="text-[0.7rem] text-[#0A0A0A]/55 mb-3">
                    {PAYMENT_MODE_LABEL[r.payment_mode] || r.payment_mode}
                    {r.payment_mode === "paid" && r.price_mode === "fixed" && r.price_amount ? (
                      <span className="text-[#B8922A]"> · {r.price_amount.toLocaleString("fr-FR")} FCFA/pers.</span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => copyLink(r.slug || r.shareable_token)}
                      className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.18em] text-[#B8922A] hover:text-[#9d7a23] border border-[#B8922A]/30 px-2.5 py-1.5"
                      data-testid={`copy-link-${r.id.slice(0, 8)}`}
                      title={publicUrl(r.slug || r.shareable_token)}
                    >
                      <Copy size={11} /> Copier lien
                    </button>
                    <a
                      href={publicUrl(r.slug || r.shareable_token)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#B8922A] border border-[#0A0A0A]/15 px-2.5 py-1.5"
                      data-testid={`preview-link-${r.id.slice(0, 8)}`}
                    >
                      <ExternalLink size={11} /> Aperçu
                    </a>
                    <button
                      onClick={() => openDetail(r.id)}
                      className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#B8922A] border border-[#0A0A0A]/15 px-2.5 py-1.5"
                      data-testid={`open-detail-${r.id.slice(0, 8)}`}
                    >
                      <BarChart3 size={11} /> Détail
                    </button>
                    <button
                      onClick={() => downloadExport(r.id, "csv")}
                      className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#B8922A] border border-[#0A0A0A]/15 px-2.5 py-1.5"
                      data-testid={`export-csv-${r.id.slice(0, 8)}`}
                    >
                      <FileDown size={11} /> CSV
                    </button>
                    <button
                      onClick={() => downloadExport(r.id, "xlsx")}
                      className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#B8922A] border border-[#0A0A0A]/15 px-2.5 py-1.5"
                      data-testid={`export-xlsx-${r.id.slice(0, 8)}`}
                    >
                      <FileDown size={11} /> Excel
                    </button>
                    <button
                      onClick={() => downloadExport(r.id, "pdf")}
                      className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#B8922A] border border-[#0A0A0A]/15 px-2.5 py-1.5"
                      data-testid={`export-pdf-${r.id.slice(0, 8)}`}
                    >
                      <FileText size={11} /> PDF
                    </button>
                    {isManager && (
                      <button
                        onClick={() => remove(r.id)}
                        className="ml-auto text-[#0A0A0A]/40 hover:text-red-600 p-1.5"
                        data-testid={`delete-corporate-${r.id.slice(0, 8)}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" data-testid="create-corporate-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display-serif text-xl text-[#0A0A0A]">Nouveau lien d&apos;inscription</h2>
              <button onClick={() => setCreating(false)} className="text-[#0A0A0A]/55 hover:text-[#0A0A0A]"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Nom du groupe ou de l&apos;événement *</label>
                  <input
                    type="text"
                    value={form.group_name}
                    onChange={(e) => setForm({ ...form, group_name: e.target.value })}
                    placeholder="ex. Séminaire Acme · Anniversaire Sarah · Team-building MTN"
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-group_name"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    placeholder="Petit descriptif affiché aux participants…"
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-description"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Type d&apos;événement *</label>
                  <input
                    type="text"
                    value={form.reservation_type}
                    onChange={(e) => setForm({ ...form, reservation_type: e.target.value })}
                    placeholder="ex. Séminaire, Team-building, Anniversaire, Sortie beach"
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-reservation_type"
                  />
                </div>
                <div>
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Date de l&apos;événement</label>
                  <input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-event_date"
                  />
                </div>
                <div>
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Heure</label>
                  <input
                    type="time"
                    value={form.event_time}
                    onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-event_time"
                  />
                </div>
                <div>
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Heure du bateau (facultatif)</label>
                  <input
                    type="time"
                    value={form.boat_time}
                    onChange={(e) => setForm({ ...form, boat_time: e.target.value })}
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-boat_time"
                  />
                </div>
                <div>
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Nombre max. de participants *</label>
                  <input
                    type="number"
                    min="1"
                    max="2000"
                    value={form.max_participants}
                    onChange={(e) => setForm({ ...form, max_participants: parseInt(e.target.value || 0) })}
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-max_participants"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <MediaField
                  label="Image de couverture (facultative)"
                  value={form.cover_image}
                  onChange={(url) => setForm({ ...form, cover_image: url || "" })}
                  kind="image"
                  accept="image/*"
                  testid="new-corp-cover_image"
                  hint="Affichée sur la page publique, dans l'email de confirmation, et sur le pass d'embarquement."
                />
              </div>

              <div className="border-t border-[#0A0A0A]/10 pt-4 mt-4">
                <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Mode de paiement</label>
                <select
                  value={form.payment_mode}
                  onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
                  className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none mb-3"
                  data-testid="new-corp-payment-mode"
                >
                  <option value="free">Gratuit — pas de paiement</option>
                  <option value="paid">Payant — paiement en ligne</option>
                  <option value="configurable">Configurable par le participant</option>
                </select>
                {form.payment_mode === "paid" && (
                  <div className="space-y-3 bg-[#FAFAF7] p-3 border border-[#0A0A0A]/8">
                    <div>
                      <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Type de prix</label>
                      <div className="flex gap-3">
                        <label className="text-sm flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="price_mode"
                            value="fixed"
                            checked={form.price_mode === "fixed"}
                            onChange={(e) => setForm({ ...form, price_mode: e.target.value })}
                            data-testid="new-corp-price_mode-fixed"
                          />
                          Prix fixe
                        </label>
                        <label className="text-sm flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="price_mode"
                            value="per_offer"
                            checked={form.price_mode === "per_offer"}
                            onChange={(e) => setForm({ ...form, price_mode: e.target.value })}
                            data-testid="new-corp-price_mode-per_offer"
                          />
                          Selon offre (choix du participant)
                        </label>
                      </div>
                    </div>
                    {form.price_mode === "fixed" && (
                      <div>
                        <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">
                          Montant par participant (FCFA) *
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="500"
                          value={form.price_amount || ""}
                          onChange={(e) => setForm({ ...form, price_amount: parseInt(e.target.value || 0) })}
                          placeholder="ex. 25000"
                          className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                          data-testid="new-corp-price_amount"
                        />
                        <p className="text-[0.65rem] text-[#0A0A0A]/55 mt-1">
                          Le participant sera automatiquement redirigé vers la page de paiement après l&apos;inscription.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-[#0A0A0A]/10 pt-4 mt-2">
                <div>
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Email contact</label>
                  <input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-contact_email"
                  />
                </div>
                <div>
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Téléphone contact</label>
                  <input
                    type="tel"
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-contact_phone"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Notes internes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2}
                    className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                    data-testid="new-corp-notes"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setCreating(false)} className="px-4 py-2 text-sm text-[#0A0A0A]/70 hover:text-[#0A0A0A]">Annuler</button>
              <button onClick={create} className="btn-gold" data-testid="submit-create-corporate">Créer le lien</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="corporate-detail-modal">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-display-serif text-xl text-[#0A0A0A]">
                  {selected.group_name || selected.company_name}
                </h2>
                <div className="text-[0.7rem] text-[#0A0A0A]/55 mt-0.5">
                  {selected.reservation_type}
                  {selected.event_date ? ` · ${selected.event_date}` : ""}
                  {selected.event_time ? ` · ${selected.event_time}` : ""}
                </div>
              </div>
              <button onClick={closeDetail} className="text-[#0A0A0A]/55 hover:text-[#0A0A0A]"><X size={16} /></button>
            </div>
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="border border-[#0A0A0A]/8 p-3">
                  <div className="text-[0.55rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Inscrits</div>
                  <div className="font-display-serif text-2xl mt-1">{stats.total}</div>
                </div>
                {Object.entries(stats.by_kind).map(([k, v]) => (
                  <div key={k} className="border border-[#0A0A0A]/8 p-3">
                    <div className="text-[0.55rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">{k}</div>
                    <div className="font-display-serif text-2xl mt-1">{v}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 border-b border-[#0A0A0A]/10">
                    <th className="text-left py-2 pr-3">Nom</th>
                    <th className="text-left py-2 px-3">Type</th>
                    <th className="text-left py-2 px-3">Email</th>
                    <th className="text-left py-2 px-3">Téléphone</th>
                    <th className="text-left py-2 px-3">WhatsApp</th>
                    <th className="text-left py-2 pl-3">Nationalité</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.participants || []).map((p) => (
                    <tr key={p.id} className="border-b border-[#0A0A0A]/5 last:border-0">
                      <td className="py-2 pr-3">{p.surname} {p.name}</td>
                      <td className="py-2 px-3 capitalize">{p.kind}</td>
                      <td className="py-2 px-3">{p.email}</td>
                      <td className="py-2 px-3">{p.phone}</td>
                      <td className="py-2 px-3">{p.whatsapp || "—"}</td>
                      <td className="py-2 pl-3">{p.nationality}</td>
                    </tr>
                  ))}
                  {(!selected.participants || selected.participants.length === 0) && (
                    <tr><td colSpan={6} className="py-6 text-center text-[#0A0A0A]/45">Aucun participant.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
